import { useMemo } from 'react';
import type { JobsTotals } from '@mnemo/shared';
import type { Locale, TranslationKey } from '../i18n.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * La ligne des totaux du filtre — pages, couleur, noir-blanc, montant (VUE-19, VUE-22).
 *
 * Le montant est la somme des prix que uniFLOW a calculés, au tarif de chaque école : rien
 * n'est appliqué ici. Il vient en dernier et en accent, c'est le chiffre de la facture.
 */
export function TotalsStrip({
  t,
  locale,
  lead,
  totals,
}: {
  t: T;
  locale: Locale;
  /** Le premier élément : « 2 040 travaux », « 16 personnes · 1 466 travaux »… */
  lead: string;
  totals: JobsTotals;
}) {
  const tag = locale === 'de' ? 'de-CH' : 'fr-CH';
  const numbers = useMemo(() => new Intl.NumberFormat(tag), [tag]);
  const money = useMemo(
    () => new Intl.NumberFormat(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    [tag],
  );

  return (
    <dl
      aria-live="polite"
      className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-b border-border bg-bg/60 px-3 py-2 text-sm"
    >
      <div className="flex items-baseline gap-1.5">
        <dt className="sr-only">{t('totals.jobsLabel')}</dt>
        <dd className="font-semibold tabular-nums">{lead}</dd>
      </div>
      <div className="flex items-baseline gap-1.5">
        <dt className="text-muted">{t('totals.pages')}</dt>
        <dd className="text-base font-semibold tabular-nums">{numbers.format(totals.pages)}</dd>
      </div>
      <div className="flex items-baseline gap-1.5">
        <dt className="text-muted">{t('totals.color')}</dt>
        <dd className="font-semibold tabular-nums">
          {numbers.format(totals.pagesColor)}
          {totals.pages > 0 && (
            <span className="ml-1 text-xs font-normal text-muted">
              ({Math.round((totals.pagesColor / totals.pages) * 100)} %)
            </span>
          )}
        </dd>
      </div>
      <div className="flex items-baseline gap-1.5">
        <dt className="text-muted">{t('totals.bw')}</dt>
        <dd className="font-semibold tabular-nums">{numbers.format(totals.pagesBw)}</dd>
      </div>
      <div className="ml-auto flex items-baseline gap-1.5" title={t('totals.amountHint')}>
        <dt className="text-muted">{t('totals.amount')}</dt>
        <dd className="text-base font-bold tabular-nums text-accent">
          {money.format(totals.price)} CHF
        </dd>
      </div>
    </dl>
  );
}
